/**
 * AliasesSection (TRA-1154): every id clients can send for this model,
 * edited inside the model dialog like the fallback chain. The primary is
 * the first row; the others are aliases of it. Add, remove, and make
 * primary are immediate gateway edits, not part of the form save.
 */

import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  useAddModelAlias,
  usePromoteModelAlias,
  useRemoveModelAlias,
  type Model,
  type ModelScope,
} from '@/hooks/useModels'
import { SLUG_REGEX } from '@/lib/slug'

interface AliasesSectionProps {
  model: Model
  scope: ModelScope
  disabled: boolean
  /** Primary slug as typed in the form; saved with the form, not live. */
  slug: string
  slugValid: boolean
  onSlugChange: (slug: string) => void
}

type Pending = { kind: 'remove'; slug: string } | { kind: 'promote'; slug: string } | null

export function AliasesSection({
  model,
  scope,
  disabled,
  slug: primarySlug,
  slugValid: primaryValid,
  onSlugChange,
}: AliasesSectionProps): React.JSX.Element {
  const addMutation = useAddModelAlias(scope)
  const removeMutation = useRemoveModelAlias(scope)
  const promoteMutation = usePromoteModelAlias(scope)
  const [adding, setAdding] = useState(false)
  const [slug, setSlug] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)

  const teamPrefix = model.gateway_model_id.slice(0, -model.slug.length)
  const slugValid = SLUG_REGEX.test(slug)
  const busy =
    disabled || addMutation.isPending || removeMutation.isPending || promoteMutation.isPending

  async function handleAdd(): Promise<void> {
    if (!slugValid) return
    try {
      await addMutation.mutateAsync({ modelId: model.id, data: { slug } })
      toast.success(`Alias ${slug} added`)
      setSlug('')
      setAdding(false)
      setAddError(null)
    } catch (error) {
      setAddError(getApiErrorDetail(error) ?? 'Request failed')
    }
  }

  async function handleConfirm(): Promise<void> {
    if (pending === null) return
    setConfirmError(null)
    try {
      if (pending.kind === 'remove') {
        await removeMutation.mutateAsync({ modelId: model.id, slug: pending.slug })
        toast.success(`Alias ${pending.slug} removed`)
      } else {
        await promoteMutation.mutateAsync({ modelId: model.id, slug: pending.slug })
        // The form's slug state was seeded from the old primary; without
        // this, Save would rename the model straight back.
        onSlugChange(pending.slug)
        toast.success(`${pending.slug} is now the primary id`)
      }
      setPending(null)
    } catch (error) {
      setConfirmError(getApiErrorDetail(error) ?? 'Request failed')
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="slug">Model ids</Label>
      <p className="text-xs text-muted-foreground">
        Clients can send any of these as the model field. All of them use this deployment.
        The primary id saves with the form. Aliases change right away.
      </p>

      <ol className="space-y-1">
        <li className="flex items-center gap-2 border border-border px-2 py-1">
          <span className="font-mono text-sm text-muted-foreground">{teamPrefix}</span>
          {/* Borderless so the row, not the field, is the unit (matches the
              fallback chain rows). */}
          <Input
            id="slug"
            className="h-8 min-w-0 flex-1 border-0 bg-transparent px-1 font-mono focus-visible:bg-muted/40"
            value={primarySlug}
            onChange={(e) => { onSlugChange(e.target.value) }}
            maxLength={64}
            disabled={disabled}
            aria-invalid={!primaryValid}
            aria-label="Primary model id"
            required
          />
          <span className="flex w-36 shrink-0 justify-end pr-2 text-xs text-muted-foreground">
            Primary
          </span>
        </li>
        {model.aliases.map((alias) => (
          <li key={alias} className="flex items-center gap-2 border border-border px-2 py-1">
            <span className="min-w-0 flex-1 truncate px-1 font-mono text-sm">
              <span className="text-muted-foreground">{teamPrefix}</span>
              {alias}
            </span>
            <span className="flex w-36 shrink-0 items-center justify-end">
              {/* Plain button on purpose: the Button dialect (mono, caps)
                  would make a rare action the loudest thing in the row. */}
              <button
                type="button"
                className="px-2 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:underline focus-visible:outline-none disabled:opacity-50"
                disabled={busy}
                onClick={() => { setPending({ kind: 'promote', slug: alias }) }}
              >
                Make primary
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove alias ${alias}`}
                disabled={busy}
                onClick={() => { setPending({ kind: 'remove', slug: alias }) }}
              >
                <X className="h-4 w-4" />
              </Button>
            </span>
          </li>
        ))}
      </ol>

      {adding ? (
        <div className="flex items-center gap-2 border border-border px-2 py-1">
          <span className="font-mono text-sm text-muted-foreground">{teamPrefix}</span>
          <Input
            id="alias-slug"
            className="h-8 min-w-0 flex-1 border-0 bg-transparent px-1 font-mono focus-visible:bg-muted/40"
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setAddError(null) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void handleAdd() }
            }}
            placeholder="my-model-v2"
            autoComplete="off"
            autoFocus
            disabled={busy}
          />
          <span className="flex w-36 shrink-0 items-center justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              disabled={busy || !slugValid}
              onClick={() => { void handleAdd() }}
            >
              {addMutation.isPending ? 'Adding...' : 'Add'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Cancel adding an alias"
              disabled={busy}
              onClick={() => { setAdding(false); setSlug(''); setAddError(null) }}
            >
              <X className="h-4 w-4" />
            </Button>
          </span>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          className="w-full justify-between font-normal text-muted-foreground"
          onClick={() => { setAdding(true) }}
        >
          Add an alias…
          <Plus className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      )}
      {!primaryValid && (
        <p className="text-sm text-destructive">
          Primary id must be lowercase alnum + hyphens/dots, start and end alphanumeric, 64 chars max.
        </p>
      )}
      {adding && slug !== '' && !slugValid && (
        <p className="text-xs text-destructive">
          Use lowercase letters, digits, hyphens, and dots. Start and end with a letter or digit.
        </p>
      )}
      {addError !== null && <p className="text-sm text-destructive">{addError}</p>}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null)
            setConfirmError(null)
          }
        }}
        title={pending?.kind === 'promote' ? 'Make primary' : 'Remove alias'}
        description={
          pending?.kind === 'promote' ? (
            <>
              <span className="font-mono">{teamPrefix}{pending.slug}</span> becomes the primary
              id. <span className="font-mono">{model.gateway_model_id}</span> keeps working as an
              alias. Requests to either id can fail for a moment while the deployment slot moves.
            </>
          ) : (
            <>
              Clients that still send{' '}
              <span className="font-mono">{teamPrefix}{pending?.slug ?? ''}</span> will get a
              model-not-found error.
            </>
          )
        }
        confirmLabel={pending?.kind === 'promote' ? 'Make primary' : 'Remove'}
        pendingLabel={pending?.kind === 'promote' ? 'Moving...' : 'Removing...'}
        destructive={pending?.kind !== 'promote'}
        isPending={removeMutation.isPending || promoteMutation.isPending}
        error={confirmError}
        onConfirm={() => { void handleConfirm() }}
      />
    </div>
  )
}
